<template>
  <div class="login container-EXPONDO">
    <h1
      class="login__title"
      data-testid="login-page-title"
    >
      {{ $t('Login') }}
    </h1>
    <div class="login__layout">
      <div class="login__form">
        <LoginForm
          :caption="$t('Log in')"
          @click-forgot-password="goToResetPassword"
        />
      </div>
      <div
        v-if="canShowContent"
        class="login__promo"
        data-testid="registerAccountBox"
      >
        <RenderContent :content="internalCmsContent" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import RenderContent from '~/cms/RenderContent.vue';
import LoginForm from '~/components/organisms/LoginForm/LoginForm.vue';
import { COMPONENTS } from '~~/constants/cms';
import { ROUTES, ROUTES_INFORMATION } from '~~/constants/routes';
import { useUser } from '~/utils/ct';
import { generateMetaTitleWithCTFormat } from '~~/helpers/commercetools/meta/generateMetaTitle';
import { isValidRedirectUrl } from '~~/helpers/url/isValidRedirectUrl';
import { getFirstChildOrSelf } from '~~/helpers/dataStructure/getFirstChildOrSelf';
import { AUTHENTICATED_QUOTE_LINK_QUERY_KEY } from '~~/constants/cartSessionTransfer';

definePageMeta({
  layout: 'no-footer-layout',
  middleware: [
    'non-authenticated',
  ],
});

export default defineComponent({
  name: 'Login',
  components: {
    LoginForm,
    RenderContent,
  },
  setup() {

    const i18n = useI18n();
    const router = useRouter();
    const route = useRoute();
    const { isAuthenticated } = useUser();
    const { getAdjustedSlug } = useRouteExtended();

    const rerouteAuthenticatedUser = () => {

      if (isAuthenticated.value) {

        return navigateTo(getAdjustedSlug(ROUTES.MY_ACCOUNT));

      }

    };

    const goToResetPassword = () => navigateTo(resetPasswordSlug.value);

    useMetaExtended({
      metaTitle: computed(() => generateMetaTitleWithCTFormat({
        title: i18n.t('Login'), i18n,
      })),
      isPageIndexed: ref(false),
      slug: ref(ROUTES_INFORMATION[ROUTES.LOGIN].SLUG),
      isPageHreflangLinkHidden: ref(true),
    });

    const {
      loadComponent,
      canShowContent,
      internalCmsContent,
    } = useInternalCms(COMPONENTS.CREATE_ACCOUNT_PROMO.SLUG);

    useAsyncData(`cms-${COMPONENTS.CREATE_ACCOUNT_PROMO.SLUG}`, async () => {

      const navigationResult = rerouteAuthenticatedUser();

      if (navigationResult) {

        return navigationResult;

      }

      await loadComponent();

      return '';

    });

    const redirectAuthenticatedUser = () => {

      const redirectTo = getFirstChildOrSelf(route.query.redirectTo);
      const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : null;

      if (isValidRedirectUrl(decodedRedirectTo)) {

        // Frozen-cart quote links need checkout-billing-frozen.ts's SSR-only logic
        // (auth check, frozen cart fetch, conflicting-cart cleanup) to run before the
        // page renders, so force a full page load instead of a client-side SPA transition.
        if (decodedRedirectTo?.includes(`${AUTHENTICATED_QUOTE_LINK_QUERY_KEY}=`)) {

          navigateTo(decodedRedirectTo, { external: true });

        } else {

          if (decodedRedirectTo) {

            router.push(decodedRedirectTo);
          
          }
        
        }

      } else {

        router.push(getAdjustedSlug(ROUTES.MY_ACCOUNT));

      }

    };

    watch(isAuthenticated, () => {

      if (isAuthenticated.value) {

        redirectAuthenticatedUser();

      }

    });

    const resetPasswordSlug = computed(() => getAdjustedSlug(ROUTES.RESET_PASSWORD));

    return {
      goToResetPassword,
      ROUTES,
      canShowContent,
      internalCmsContent,
      resetPasswordSlug,
    };

  },
});
</script>

<style lang="scss" scoped>
  .login {
    @apply my-10;
  }

  .login__title {
    @apply px-5 text-center lg:text-left;
  }

  .login__layout {
    @apply lg:flex;
  }

  .login__form {
    @apply px-5 my-7 lg:w-2/3;
  }

  .login__promo {
    @apply px-5 my-7 lg:w-1/3;
  }
</style>
